import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_URL } from './adminUtils';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  RefreshCw,
  Upload,
  Globe,
  Image as ImageIcon,
  Play,
  Link as LinkIcon,
  Loader2,
} from 'lucide-react';

const AssociationsManagement = () => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAssoc, setEditingAssoc] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  
  // Leadership state
  const [newLeader, setNewLeader] = useState({ name: '', role: '', photo_url: '', email: '', phone: '' });
  const [addingLeader, setAddingLeader] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [uploadingLeaderPhoto, setUploadingLeaderPhoto] = useState(false);
  const leaderPhotoInputRef = useRef(null);
  
  // New state for creating associations
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAssocData, setNewAssocData] = useState({
    name: '', short_name: '', slug: '', region: '', description: '', 
    mission: '', cultural_background: '', image_url: ''
  });
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const fetchAssociations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/associations`);
      setAssociations(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssociations();
  }, [fetchAssociations]);
  
  const handleCreateAssociation = async () => {
    if (!newAssocData.name || !newAssocData.short_name || !newAssocData.region || !newAssocData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/associations`, newAssocData);
      toast.success('Association created successfully');
      setShowCreateDialog(false);
      setNewAssocData({ name: '', short_name: '', slug: '', region: '', description: '', mission: '', cultural_background: '', image_url: '' });
      fetchAssociations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create association');
    } finally {
      setCreating(false);
    }
  };
  
  const handleDeleteAssociation = async (assocId, assocName) => {
    if (!window.confirm(`Are you sure you want to delete "${assocName}"? This will also delete all related signatures and activities.`)) {
      return;
    }
    setDeletingId(assocId);
    try {
      await axios.delete(`${API_URL}/associations/${assocId}`);
      toast.success('Association deleted successfully');
      fetchAssociations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete association');
    } finally {
      setDeletingId(null);
    }
  };
  
  const handleEdit = (assoc) => {
    setEditingAssoc(assoc);
    setFormData({
      name: assoc.name,
      short_name: assoc.short_name,
      slug: assoc.slug || assoc.short_name.toLowerCase(),
      description: assoc.description,
      mission: assoc.mission,
      cultural_background: assoc.cultural_background,
      region: assoc.region,
      president_name: assoc.president_name || '',
      president_photo_url: assoc.president_photo_url || '',
      president_email: assoc.president_email || '',
      image_url: assoc.image_url || '',
      gallery_images: assoc.gallery_images || [],
      youtube_videos: assoc.youtube_videos || [],
      leadership: assoc.leadership || []
    });
    setActiveTab('details');
    setNewVideoUrl('');
    setNewVideoTitle('');
    setNewLeader({ name: '', role: '', photo_url: '', email: '', phone: '' });
    setEditingLeader(null);
  };
  
  // Leadership functions
  const handleAddLeader = async () => {
    if (!newLeader.name || !newLeader.role) {
      toast.error('Please enter name and role');
      return;
    }
    setAddingLeader(true);
    try {      const response = await axios.post(
        `${API_URL}/associations/${editingAssoc.id}/leadership`,
        newLeader
      );
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        leadership: [...(prev.leadership || []), response.data.member]
      }));
      
      toast.success('Team member added');
      setNewLeader({ name: '', role: '', photo_url: '', email: '', phone: '' });
    } catch (error) {
      toast.error('Failed to add team member');
    } finally {
      setAddingLeader(false);
    }
  };
  
  const handleUpdateLeader = async (memberId) => {
    if (!editingLeader?.name || !editingLeader?.role) {
      toast.error('Please enter name and role');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/associations/${editingAssoc.id}/leadership/${memberId}`,
        editingLeader
      );
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        leadership: prev.leadership.map(m => m.id === memberId ? { ...m, ...editingLeader } : m)
      }));
      
      toast.success('Team member updated');
      setEditingLeader(null);
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };
  
  const handleDeleteLeader = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(
        `${API_URL}/associations/${editingAssoc.id}/leadership/${memberId}`
      );
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        leadership: prev.leadership.filter(m => m.id !== memberId)
      }));
      
      toast.success('Team member removed');
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };
  
  const handleLeaderPhotoUpload = async (e, isNew = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLeaderPhoto(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {      const response = await axios.post(`${API_URL}/upload/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (isNew) {
        setNewLeader(prev => ({ ...prev, photo_url: response.data.url }));
      } else if (editingLeader) {
        setEditingLeader(prev => ({ ...prev, photo_url: response.data.url }));
      }
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingLeaderPhoto(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate slug format
      const slugRegex = /^[a-z0-9-]+$/;
      if (formData.slug && !slugRegex.test(formData.slug)) {
        toast.error('Slug can only contain lowercase letters, numbers, and hyphens');
        setSaving(false);
        return;
      }
      
      await axios.put(`${API_URL}/associations/${editingAssoc.id}`, formData);
      toast.success('Association updated successfully');
      setEditingAssoc(null);
      fetchAssociations();
    } catch (error) {
      toast.error('Failed to update association');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddVideo = async () => {
    if (!newVideoUrl) {
      toast.error('Please enter a YouTube URL');
      return;
    }
    
    setAddingVideo(true);
    try {      const params = new URLSearchParams({
        url: newVideoUrl,
        title: newVideoTitle,
        description: ''
      });
      
      await axios.post(
        `${API_URL}/associations/${editingAssoc.id}/videos?${params.toString()}`,
        {}
      );
      
      toast.success('Video added successfully');
      setNewVideoUrl('');
      setNewVideoTitle('');
      
      // Refresh association data
      const response = await axios.get(`${API_URL}/associations/${editingAssoc.id}`);
      setEditingAssoc(response.data);
      setFormData(prev => ({ ...prev, youtube_videos: response.data.youtube_videos || [] }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video');
    } finally {
      setAddingVideo(false);
    }
  };
  
  const handleRemoveVideo = async (index) => {
    try {
      await axios.delete(
        `${API_URL}/associations/${editingAssoc.id}/videos/${index}`
      );
      
      toast.success('Video removed');
      
      // Update local state
      const updatedVideos = [...formData.youtube_videos];
      updatedVideos.splice(index, 1);
      setFormData(prev => ({ ...prev, youtube_videos: updatedVideos }));
    } catch (error) {
      toast.error('Failed to remove video');
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a JPEG, PNG, WebP, or GIF image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingImage(true);
    try {      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Handle both S3 URLs (complete) and local URLs (relative)
      let imageUrl = response.data.url;
      if (response.data.storage === 'local' && !imageUrl.startsWith('http')) {
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      }
      
      // Add to gallery
      await axios.post(
        `${API_URL}/associations/${editingAssoc.id}/gallery?image_url=${encodeURIComponent(imageUrl)}&caption=`,
        {}
      );
      
      toast.success(`Image uploaded to ${response.data.storage === 's3' ? 'S3' : 'server'} and added to gallery`);
      
      // Refresh association data
      const assocResponse = await axios.get(`${API_URL}/associations/${editingAssoc.id}`);
      setEditingAssoc(assocResponse.data);
      setFormData(prev => ({ ...prev, gallery_images: assocResponse.data.gallery_images || [] }));
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveGalleryImage = async (index) => {
    try {
      await axios.delete(
        `${API_URL}/associations/${editingAssoc.id}/gallery/${index}`
      );
      
      toast.success('Image removed from gallery');
      
      // Update local state
      const updatedImages = [...formData.gallery_images];
      updatedImages.splice(index, 1);
      setFormData(prev => ({ ...prev, gallery_images: updatedImages }));
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a JPEG, PNG, WebP, or GIF image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingCoverImage(true);
    try {      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Handle both S3 URLs (complete) and local URLs (relative)
      let imageUrl = response.data.url;
      if (response.data.storage === 'local' && !imageUrl.startsWith('http')) {
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      }
      
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success(`Cover image uploaded to ${response.data.storage === 's3' ? 'S3' : 'server'}`);
    } catch (error) {
      toast.error('Failed to upload cover image');
    } finally {
      setUploadingCoverImage(false);
      if (coverImageInputRef.current) coverImageInputRef.current.value = '';
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold">Manage Associations</h1>
          <p className="text-muted-foreground">Edit details, gallery, videos, and custom URLs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAssociations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-cameroon-green hover:bg-cameroon-green/90">
            <Plus className="w-4 h-4 mr-2" />
            Add Association
          </Button>
        </div>
      </div>
      
      {/* Create Association Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Add New Association</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input 
                  value={newAssocData.name}
                  onChange={(e) => setNewAssocData(prev => ({...prev, name: e.target.value}))}
                  placeholder="e.g., Bafut Cultural Association"
                />
              </div>
              <div className="space-y-2">
                <Label>Short Name *</Label>
                <Input 
                  value={newAssocData.short_name}
                  onChange={(e) => setNewAssocData(prev => ({...prev, short_name: e.target.value.toUpperCase()}))}
                  placeholder="e.g., BAFUT"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>URL Slug</Label>
                <Input 
                  value={newAssocData.slug}
                  onChange={(e) => setNewAssocData(prev => ({...prev, slug: e.target.value.toLowerCase().replace(/\s/g, '-')}))}
                  placeholder="e.g., bafut"
                />
              </div>
              <div className="space-y-2">
                <Label>Region *</Label>
                <Select value={newAssocData.region} onValueChange={(v) => setNewAssocData(prev => ({...prev, region: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Northwest">Northwest</SelectItem>
                    <SelectItem value="Southwest">Southwest</SelectItem>
                    <SelectItem value="West">West</SelectItem>
                    <SelectItem value="East/South">East/South</SelectItem>
                    <SelectItem value="National">National</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea 
                value={newAssocData.description}
                onChange={(e) => setNewAssocData(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description of the association"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Mission</Label>
              <Textarea 
                value={newAssocData.mission}
                onChange={(e) => setNewAssocData(prev => ({...prev, mission: e.target.value}))}
                placeholder="Association's mission statement"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Cover Image URL</Label>
              <Input 
                value={newAssocData.image_url}
                onChange={(e) => setNewAssocData(prev => ({...prev, image_url: e.target.value}))}
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAssociation} disabled={creating} className="bg-cameroon-green hover:bg-cameroon-green/90">
              {creating ? 'Creating...' : 'Create Association'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {associations.map((assoc) => (
          <Card key={assoc.id} className="overflow-hidden">
            <div className="h-32 relative">
              <img 
                src={assoc.image_url || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7'}
                alt={assoc.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <Badge 
                className={`absolute top-2 right-2 ${assoc.signature_status === 'signed' ? 'bg-cameroon-green' : 'bg-cameroon-yellow text-black'}`}
              >
                {assoc.signature_status === 'signed' ? 'Signed' : 'Pending'}
              </Badge>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 left-2 h-6 w-6"
                onClick={() => handleDeleteAssociation(assoc.id, assoc.name)}
                disabled={deletingId === assoc.id}
              >
                {deletingId === assoc.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </Button>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold">{assoc.short_name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-1">{assoc.name}</p>
              <div className="flex items-center gap-1 mt-1">
                <Globe className="w-3 h-3 text-muted-foreground" />
                <code className="text-xs text-cameroon-green">/{assoc.slug || assoc.short_name.toLowerCase()}</code>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {assoc.gallery_images?.length || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  {assoc.youtube_videos?.length || 0}
                </span>
              </div>
              
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3"
                    onClick={() => handleEdit(assoc)}
                    data-testid={`edit-assoc-${assoc.slug}`}
                  >
                    <Edit className="w-3 h-3 mr-2" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif">Edit {editingAssoc?.short_name}</DialogTitle>
                  </DialogHeader>
                  
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                    <TabsList className="w-full grid grid-cols-4">
                      <TabsTrigger value="details">Details</TabsTrigger>
                      <TabsTrigger value="leadership">Leadership ({formData.leadership?.length || 0})</TabsTrigger>
                      <TabsTrigger value="gallery">Gallery ({formData.gallery_images?.length || 0})</TabsTrigger>
                      <TabsTrigger value="videos">Videos ({formData.youtube_videos?.length || 0})</TabsTrigger>
                    </TabsList>
                    
                    {/* Details Tab */}
                    <TabsContent value="details" className="space-y-4 mt-4">
                      {/* Slug/URL Section */}
                      <Card className="border-cameroon-green/30 bg-cameroon-green/5">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <LinkIcon className="w-4 h-4 text-cameroon-green" />
                            <Label className="text-sm font-medium">Custom URL / Slug</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground text-sm">{window.location.origin}/</span>
                            <Input
                              value={formData.slug || ''}
                              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                              placeholder="association-slug"
                              className="flex-1 font-mono"
                              data-testid="slug-input"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Only lowercase letters, numbers, and hyphens allowed. This is the shareable URL.
                          </p>
                        </CardContent>
                      </Card>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Short Name</Label>
                          <Input
                            value={formData.short_name || ''}
                            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Region</Label>
                          <Input
                            value={formData.region || ''}
                            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mission</Label>
                        <Textarea
                          value={formData.mission || ''}
                          onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                          rows={2}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Cultural Background</Label>
                        <Textarea
                          value={formData.cultural_background || ''}
                          onChange={(e) => setFormData({ ...formData, cultural_background: e.target.value })}
                          rows={3}
                        />
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>President Name</Label>
                          <Input
                            value={formData.president_name || ''}
                            onChange={(e) => setFormData({ ...formData, president_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cover Image</Label>
                          <div className="flex gap-2">
                            <Input
                              value={formData.image_url || ''}
                              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                              placeholder="https://... or upload"
                              className="flex-1"
                            />
                            <input
                              ref={coverImageInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleCoverImageUpload}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => coverImageInputRef.current?.click()}
                              disabled={uploadingCoverImage}
                              title="Upload image"
                              data-testid="upload-cover-image-btn"
                            >
                              {uploadingCoverImage ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          {formData.image_url && (
                            <div className="mt-2 relative group w-full aspect-video rounded-lg overflow-hidden bg-muted">
                              <img 
                                src={formData.image_url}
                                alt="Cover preview"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => setFormData({ ...formData, image_url: '' })}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Leadership Tab */}
                    <TabsContent value="leadership" className="space-y-4 mt-4">
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Manage leadership team members with their names and photos
                        </p>
                        
                        {/* President Section */}
                        <Card className="border-cameroon-green/30 bg-cameroon-green/5">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <Users className="w-4 h-4 text-cameroon-green" />
                              President
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex items-center gap-4">
                              {formData.president_photo_url ? (
                                <img 
                                  src={formData.president_photo_url} 
                                  alt="President"
                                  className="w-16 h-16 rounded-full object-cover border-2 border-cameroon-green"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                  <Users className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                              <div className="flex-1 space-y-2">
                                <div>
                                  <Label className="text-xs">President Name</Label>
                                  <Input
                                    value={formData.president_name || ''}
                                    onChange={(e) => setFormData({ ...formData, president_name: e.target.value })}
                                    placeholder="Enter president name"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">President Email</Label>
                                  <Input
                                    value={formData.president_email || ''}
                                    onChange={(e) => setFormData({ ...formData, president_email: e.target.value })}
                                    placeholder="president@example.com"
                                  />
                                </div>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">President Photo URL</Label>
                              <Input
                                value={formData.president_photo_url || ''}
                                onChange={(e) => setFormData({ ...formData, president_photo_url: e.target.value })}
                                placeholder="https://... or upload below"
                              />
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* Add New Team Member */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium">Add Team Member</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Name *</Label>
                                <Input
                                  value={newLeader.name}
                                  onChange={(e) => setNewLeader({ ...newLeader, name: e.target.value })}
                                  placeholder="Full name"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Role/Position *</Label>
                                <Input
                                  value={newLeader.role}
                                  onChange={(e) => setNewLeader({ ...newLeader, role: e.target.value })}
                                  placeholder="e.g., Vice President, Secretary"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Email</Label>
                                <Input
                                  type="email"
                                  value={newLeader.email}
                                  onChange={(e) => setNewLeader({ ...newLeader, email: e.target.value })}
                                  placeholder="email@example.com"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Phone</Label>
                                <Input
                                  value={newLeader.phone}
                                  onChange={(e) => setNewLeader({ ...newLeader, phone: e.target.value })}
                                  placeholder="+1 (xxx) xxx-xxxx"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Photo URL</Label>
                              <div className="flex gap-2">
                                <Input
                                  value={newLeader.photo_url}
                                  onChange={(e) => setNewLeader({ ...newLeader, photo_url: e.target.value })}
                                  placeholder="https://... or upload"
                                  className="flex-1"
                                />
                                <input
                                  ref={leaderPhotoInputRef}
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={(e) => handleLeaderPhotoUpload(e, true)}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => leaderPhotoInputRef.current?.click()}
                                  disabled={uploadingLeaderPhoto}
                                >
                                  {uploadingLeaderPhoto ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                            {newLeader.photo_url && (
                              <div className="flex items-center gap-2">
                                <img src={newLeader.photo_url} alt="Preview" className="w-12 h-12 rounded-full object-cover" />
                                <span className="text-xs text-muted-foreground">Photo preview</span>
                              </div>
                            )}
                            <Button
                              onClick={handleAddLeader}
                              disabled={addingLeader || !newLeader.name || !newLeader.role}
                              className="w-full"
                            >
                              {addingLeader ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                              Add Team Member
                            </Button>
                          </CardContent>
                        </Card>
                        
                        {/* Existing Team Members */}
                        {formData.leadership && formData.leadership.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium">Current Team Members ({formData.leadership.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-3">
                                {formData.leadership.map((member) => (
                                  <div key={member.id} className="flex items-center gap-3 p-3 border rounded-lg">
                                    {member.photo_url ? (
                                      <img src={member.photo_url} alt={member.name} className="w-12 h-12 rounded-full object-cover" />
                                    ) : (
                                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                        <span className="font-bold text-muted-foreground">{member.name?.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      {editingLeader?.id === member.id ? (
                                        <div className="space-y-2">
                                          <div className="grid grid-cols-2 gap-2">
                                            <Input
                                              value={editingLeader.name}
                                              onChange={(e) => setEditingLeader({ ...editingLeader, name: e.target.value })}
                                              placeholder="Name"
                                              className="h-8 text-sm"
                                            />
                                            <Input
                                              value={editingLeader.role}
                                              onChange={(e) => setEditingLeader({ ...editingLeader, role: e.target.value })}
                                              placeholder="Role"
                                              className="h-8 text-sm"
                                            />
                                          </div>
                                          <Input
                                            value={editingLeader.photo_url || ''}
                                            onChange={(e) => setEditingLeader({ ...editingLeader, photo_url: e.target.value })}
                                            placeholder="Photo URL"
                                            className="h-8 text-sm"
                                          />
                                          <div className="flex gap-2">
                                            <Button size="sm" onClick={() => handleUpdateLeader(member.id)}>Save</Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditingLeader(null)}>Cancel</Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="font-medium truncate">{member.name}</p>
                                          <p className="text-sm text-muted-foreground">{member.role}</p>
                                          {member.email && <p className="text-xs text-muted-foreground">{member.email}</p>}
                                        </>
                                      )}
                                    </div>
                                    {editingLeader?.id !== member.id && (
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setEditingLeader({ ...member })}
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-destructive hover:text-destructive"
                                          onClick={() => handleDeleteLeader(member.id)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </TabsContent>
                    
                    {/* Gallery Tab */}
                    <TabsContent value="gallery" className="space-y-4 mt-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Manage gallery images for this association
                        </p>
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            data-testid="add-gallery-image-btn"
                          >
                            {uploadingImage ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4 mr-2" />
                            )}
                            {uploadingImage ? 'Uploading...' : 'Add Image'}
                          </Button>
                        </div>
                      </div>
                      
                      {formData.gallery_images?.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {formData.gallery_images.map((img, index) => (
                            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
                              <img 
                                src={img.url}
                                alt={img.caption || `Gallery ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleRemoveGalleryImage(index)}
                                  data-testid={`remove-gallery-${index}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                          <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground">No gallery images yet</p>
                          <p className="text-sm text-muted-foreground">Upload images to showcase cultural events</p>
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Videos Tab */}
                    <TabsContent value="videos" className="space-y-4 mt-4">
                      {/* Add Video Form */}
                      <Card>
                        <CardContent className="p-4 space-y-3">
                          <Label>Add YouTube Video</Label>
                          <div className="flex gap-2">
                            <Input
                              value={newVideoUrl}
                              onChange={(e) => setNewVideoUrl(e.target.value)}
                              placeholder="https://www.youtube.com/watch?v=..."
                              className="flex-1"
                              data-testid="video-url-input"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={newVideoTitle}
                              onChange={(e) => setNewVideoTitle(e.target.value)}
                              placeholder="Video title (optional)"
                              className="flex-1"
                            />
                            <Button
                              onClick={handleAddVideo}
                              disabled={addingVideo || !newVideoUrl}
                              data-testid="add-video-btn"
                            >
                              {addingVideo ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {formData.youtube_videos?.length > 0 ? (
                        <div className="space-y-4">
                          {formData.youtube_videos.map((video, index) => (
                            <div key={video.video_id || `video-${index}`} className="flex gap-4 p-4 border rounded-lg">
                              <div className="w-40 aspect-video flex-shrink-0 rounded overflow-hidden bg-muted">
                                <img 
                                  src={`https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
                                  alt={video.title || 'Video thumbnail'}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{video.title || 'Untitled Video'}</h4>
                                <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveVideo(index)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`remove-video-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                          <Play className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground">No videos yet</p>
                          <p className="text-sm text-muted-foreground">Add YouTube videos to showcase activities</p>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                  
                  <div className="flex gap-3 pt-4 border-t mt-4">
                    <Button 
                      onClick={handleSave} 
                      disabled={saving}
                      className="bg-cameroon-green hover:bg-cameroon-green/90"
                      data-testid="save-association-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save All Changes'}
                    </Button>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AssociationsManagement;
